import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Avatar } from 'antd';
import { Popover } from '@blueprintnyc/bpnyc-common-admin';
import { ArrowRight, Logout, Globe } from '@blueprintnyc/bpnyc-common-admin/dist/icons';
import { logout } from '../../redux/actions';
import logo from '../../assets/thalo_logo.png';
import collapsedLogo from '../../assets/thalo_collapsed.png';
import { captureEvent } from 'utils';
import posthog from 'posthog-js';
import './SideMenu.scss';

const { Sider } = Layout;

const SideMenu = props => {
	const { menuData, client, event, adminSSOProfile, logout } = props;
	const navigate = useNavigate();
	const location = useLocation();

	// 992 corresponds to 'lg breakpoint' defined below in <Sider /> props
	const [collapsed, setCollapsed] = useState(window.innerWidth < 992);
	const [selectedKey, setSelectedKey] = useState(null);
	const shouldCollapse =
		location && (location.pathname.includes('widgetHome') || location.pathname.includes('pages/'));

	const staging = import.meta.env.MODE === 'staging';

	useEffect(() => {
		setMenuItemSelected(window.location.pathname);
	}, []);

	useEffect(() => {
		shouldCollapse ? setCollapsed(true) : setCollapsed(false);
	}, [location]);

	const setMenuItemSelected = key => {
		setSelectedKey(key);
	};

	const onCollapse = collapsed => {
		if (!shouldCollapse) setCollapsed(collapsed);
	};
	const handleLogout = () => {
		captureEvent('logout');
		posthog.reset();
		logout();
	};

	const handleClick = item => {
		const { path } = item;
		setMenuItemSelected(path);
		if (item.name === 'Logout') handleLogout();
		else if (path.charAt(0) === '/') navigate(path);
		if (!collapsed && window.innerWidth < 992) setCollapsed(true);
	};

	const handleLogoClick = () => {
		navigate('/events', { state: { from: 'SideMenu' } });
	};

	const renderEventBadge = item => {
		const selected = selectedKey === item.path;
		const className = `event-badge ${collapsed ? 'collapsed' : ''} ${selected ? 'selected' : ''}`;
		return (
			<div className={className} key="eventBadge" onClick={() => handleClick(item)}>
				{event.event_image_url ? <img src={event.event_image_url} alt={event.name} /> : null}
				{!collapsed && (
					<div className="event-details">
						<span className="event-name">{event.name}</span>
						<span className="client-name">{client.name}</span>
					</div>
				)}
			</div>
		);
	};

	const renderMenuItem = (item, isSubItem) => {
		const selected = selectedKey === item.path;
		const title = item.name;
		const className = `menu-item ${isSubItem ? 'sub-item' : ''} ${selected ? 'selected' : ''} ${
			collapsed ? 'collapsed' : ''
		} `;
		// if (title === 'Admins' && !isSuperAdmin()) return null;
		return (
			<div
				className={className}
				key={`menuItem-${title}${isSubItem ? '-sub' : ''}`}
				onClick={() => handleClick(item)}
				style={{ justifyContent: collapsed && !isSubItem ? 'center' : '' }}
			>
				<div className={`overlay ${collapsed ? 'collapsed' : ''}`} />
				{item.icon ? <div className="icon">{item.icon}</div> : <div className="no-icon" />}
				{(!collapsed || isSubItem) && <span className="menu-title">{title}</span>}
			</div>
		);
	};

	const renderSubmenuItem = item => {
		const selected = selectedKey === item.path;
		const title = item.name;
		const content = (
			<div
				className="sub-menu"
				style={{ backgroundColor: staging ? '#d32463' : '#00108e' }}
				key={title + '-subMenu'}
			>
				{item.items.map(subItem => renderMenuItem(subItem, true))}
			</div>
		);
		return (
			<Popover
				closeOnBackground
				placement="right"
				content={content}
				key={'subMenuItem-' + title}
				hideArrow
			>
				<div className={`menu-item ${selected ? 'selected' : ''}`}>
					<div className={`overlay ${collapsed ? 'collapsed' : ''}`} />
					{item.icon ? <div className="icon">{item.icon}</div> : <div className="no-icon" />}
					{!collapsed && (
						<>
							<span className="menu-title">{title}</span>
							<div className="icon right">
								<ArrowRight fontSize={12} />
							</div>
						</>
					)}
				</div>
			</Popover>
		);
	};

	const renderUserItem = () => {
		const subMenu = [
			{
				name: 'All Events',
				icon: <Globe />,
				path: '/events',
			},
			// {
			// 	name: 'Admins',
			// 	icon: <Contacts />,
			// 	path: '/admins',
			// },
			{
				name: 'Logout',
				icon: <Logout />,
				path: `/logout`,
			},
		];
		return adminSSOProfile ? (
			<Popover
				closeOnBackground
				placement="right-end"
				content={
					<div className="sub-menu" style={{ backgroundColor: staging ? '#d32463' : '#00108e' }}>
						{subMenu.map(subItem => renderMenuItem(subItem, true))}
					</div>
				}
				key={'userItem'}
				hideArrow
			>
				<div className={`menu-item user-item ${collapsed ? 'collapsed' : ''}`}>
					<Avatar
						className="avatar"
						shape="square"
						size={40}
						src={adminSSOProfile.picture}
						alt="profile-pic"
						style={{ borderRadius: '8px' }}
					/>
					{!collapsed && (
						<>
							<span className="menu-title">{`Howdy, ${adminSSOProfile.given_name ||
								adminSSOProfile.nickname}!`}</span>
							<div className="icon right">
								<ArrowRight fontSize={12} />
							</div>
						</>
					)}
				</div>
			</Popover>
		) : null;
	};

	return (
		<Sider
			breakpoint="lg"
			width={240}
			collapsedWidth={70}
			className="SideMenu"
			collapsed={collapsed}
			onCollapse={onCollapse}
		>
			<div id="SideMenu" style={{ backgroundColor: staging ? '#d32463' : '#00108e' }}>
				<div className="menu-top" style={{ width: collapsed ? '70px' : '240px' }}>
					<div className={`logo ${collapsed ? 'collapsed' : ''}`} onClick={handleLogoClick}>
						<img src={collapsed ? collapsedLogo : logo} alt="thalo logo" />
						{!collapsed && staging && <span className="staging">staging</span>}
					</div>
					{menuData.map(item => {
						if (item.name === 'Dashboard') return renderEventBadge(item);
						return item.type === 'submenu' ? renderSubmenuItem(item) : renderMenuItem(item);
					})}
				</div>
				<div className="menu-bottom" style={{ width: collapsed ? '70px' : '240px' }}>
					{renderUserItem()}
				</div>
			</div>
		</Sider>
	);
};

const msp = state => ({});

const mdp = dispatch => ({
	logout: () => dispatch(logout()),
});

export default connect(msp, mdp)(SideMenu);
